import React, { useEffect, useRef, useState } from "react";
import { Redirect, NavLink } from "react-router-dom";
import queryString from 'query-string';
import { hydrateRoute, checkIfStuck, toName, format, parseFilter, stringifyFilter } from "../../utils";
import { useGlobalValue } from "../../state/GlobalProvider";
import { Dropdown } from "../inputs";
import styles from "./Tabs.module.sass";
const Tabs = props => {
  const {
    modifiers,
    className,
    data,
    labelKey,
    routeKey,
    basePath,
    replace,
    redirectEnabler,
    showCount,
    stickyTop,
    preserveEmptyTabs,
    preserveFilters,
    concatTab
  } = props;
  const elem = useRef(null);
  const tabsContainer = useRef(null);
  const [stuck, setStuck] = useState(false);
  const [hasScroll, setHasScroll] = useState([false, false]);
  const {
    i18n,
    params: matchParams,
    location,
    history
  } = useGlobalValue();
  useEffect(() => {
    const handleScroll = () => {
      const isStuck = checkIfStuck(elem.current, "top", stuck ? 14 : 0);
      setStuck(isStuck);
    };
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    const isStuck = elem && elem.current && checkIfStuck(elem.current, "top", stuck ? 14 : 0);
    setStuck(prevState => {
      if (prevState !== isStuck) {
        return isStuck;
      }
      return prevState;
    });
    if (elem && elem.current) {
      const selected = elem.current.querySelector(`.${styles["is-selected"]}`);
      if (selected) {
        const rect = selected.getBoundingClientRect();
        if (rect.x < 0) {
          selected.parentNode.scrollLeft = selected.offsetLeft - 20;
        } else if (rect.x + rect.width > window.innerWidth) {
          const offset = rect.x + rect.width - window.innerWidth;
          selected.parentNode.scrollLeft = selected.parentNode.scrollLeft + offset + 20;
        }
      }
    }
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [elem, location, stuck]);
  useEffect(() => {
    const checkOverflows = () => {
      if (tabsContainer && tabsContainer.current) {
        const scrollWidth = tabsContainer.current.scrollWidth;
        const width = tabsContainer.current.clientWidth;
        const scrollIndicatorLeft = tabsContainer.current.scrollLeft > 0;
        const scrollIndicatorRight = tabsContainer.current.scrollLeft + width === scrollWidth ? false : scrollWidth > width;
        setHasScroll(prevState => {
          if (prevState[0] !== scrollIndicatorLeft || prevState[1] !== scrollIndicatorRight) {
            return [scrollIndicatorLeft, scrollIndicatorRight];
          }
          return prevState;
        });
      }
    };
    const tabsElement = tabsContainer.current;
    if (tabsElement) {
      tabsElement.addEventListener("scroll", checkOverflows);
    }
    window.addEventListener("resize", checkOverflows);
    checkOverflows();
    return () => {
      tabsElement && tabsElement.removeEventListener("scroll", checkOverflows);
      window.removeEventListener("resize", checkOverflows);
    };
  });
  const params = {
    ...matchParams,
    ...queryString.parse(location.search)
  };
  const options = [];
  const hydrated = hydrateRoute(basePath, {
    ...params
  });
  let urlFilters = "";
  if (preserveFilters && preserveFilters.length) {
    const filtersArray = parseFilter(params.filter);
    const filtersToUse = (filtersArray || []).filter(({
      column
    }) => preserveFilters.includes(column));
    urlFilters = filtersToUse && filtersToUse.length && Array.isArray(filtersToUse) ? stringifyFilter(filtersToUse) : "";
  }
  const lastPart = location.pathname.match(/\/$/) ? location.pathname.split("/").slice(-2)[0] : location.pathname.split("/").slice(-1)[0];
  const currentTab = (data || []).find(route => `${route[routeKey]}` === lastPart);
  const withoutTabRoute = currentTab ? hydrated.replace(new RegExp(`${currentTab[routeKey]}/?$`, "ig"), "") : hydrated;
  const filteredTabs = preserveEmptyTabs ? data || [] : (data || []).filter(elem => elem.count !== 0);
  const tabs = (filteredTabs || []).map((route, index) => {
    const tabToUse = concatTab && lastPart !== route[routeKey] ? `${lastPart}/${route[routeKey]}` : `${route[routeKey]}`;
    const path = `${withoutTabRoute}${withoutTabRoute.match(/\/$/) ? "" : "/"}${route[routeKey] === "/" ? "" : tabToUse}${urlFilters.length ? `?filter=${urlFilters}&resetFilters=${Date.now()}` : ""}`;
    options.push({
      id: path,
      name: `${i18n`${toName(route[labelKey])}`}${showCount ? ` (${format(route.count, "normal", null)})` : null}`
    });
    return /*#__PURE__*/React.createElement(NavLink, {
      key: `tab_${index}`,
      to: path,
      exact: true,
      className: styles["tab"],
      activeClassName: styles["is-selected"],
      replace: replace,
      isActive: (_, {
        pathname
      }) => {
        return currentTab[routeKey] === route[labelKey];
      }
    }, " ", /*#__PURE__*/React.createElement("small", null, i18n(`${toName(route[labelKey])}`), showCount ? ` (${format(route.count, "normal", null)})` : null));
  });
  if (!data || !labelKey || !routeKey) {
    return null;
  }
  if (redirectEnabler && !currentTab && (data || []).length && location.pathname.split("/").pop() !== `${data[0][routeKey] || ""}`) {
    const to = `${hydrated}${hydrated.match(/\/$/) ? "" : "/"}${data[0][routeKey] === "/" ? "" : data[0][routeKey]}`;
    if (to !== location.pathname) {
      return /*#__PURE__*/React.createElement(Redirect, {
        to: to,
        replace: true
      });
    }
  }
  const modifiersClasses = (modifiers || []).map(m => styles[m]).filter(m => m).join(" ");
  return /*#__PURE__*/React.createElement("div", {
    ref: elem,
    className: `${styles["root"]} ${className || ""} ${stuck ? styles["stuck"] : ""} ${modifiersClasses}`,
    style: {
      top: stickyTop
    },
    stuck: stuck ? "true" : null
  }, /*#__PURE__*/React.createElement("div", {
    className: styles["content"]
  }, /*#__PURE__*/React.createElement("span", {
    className: `${styles["left-scroll"]} ${hasScroll && hasScroll[0] ? styles["is-visible"] : ""}`
  }), /*#__PURE__*/React.createElement("div", {
    ref: tabsContainer,
    className: styles["tabs-container"]
  }, tabs), /*#__PURE__*/React.createElement("span", {
    className: `${styles["right-scroll"]} ${hasScroll && hasScroll[1] ? styles["is-visible"] : ""}`
  }, hasScroll && hasScroll[1] ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Dropdown, {
    className: styles["show-all-button"],
    options: options,
    onChange: e => {
      history.push({
        pathname: e.target.value.id
      });
    }
  })) : null)));
};
export default Tabs;